import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  Divider,
  Chip,
  IconButton,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Snackbar,
  Slide,
  Avatar
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Mic as MicIcon,
  Psychology as PsychologyIcon,
  MedicalServices as MedicalIcon,
  MonitorHeart as VitalsIcon,
  Assignment as DiagnosisIcon,
  Medication as MedicationIcon,
  Draw as SignIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  LocalHospital as HospitalIcon,
  Person as PersonIcon,
  Healing as HealingIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { NaraSimpleTypewriter, NaraTerminalCard, monoStyle } from './NaraTerminal';

const API_URL = 'https://turismo.nara.chat/api';

// Transición para diálogos
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Panel de Tab
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Estilo común para TextFields
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    background: '#f8fafc',
    '&:hover': { background: '#f1f5f9' },
    '&.Mui-focused': { background: '#fff' }
  }
};

// Componente de sección con título
const SeccionFormulario = ({ icon, titulo, color = "#6366f1", children }) => (
  <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
    <Typography sx={{
      fontWeight: 600,
      color: "#1e3a5f",
      fontSize: 14,
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
      {titulo}
    </Typography>
    {children}
  </Box>
);

const ConsultaForm = ({
  open,
  onClose,
  expedienteId,
  paciente,
  userId,
  onConsultaCreated,
  datosIniciales
}) => {
  const [tabActual, setTabActual] = useState(0);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [consultaId, setConsultaId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // NARA Post-Consulta
  const [naraSugerencias, setNaraSugerencias] = useState(null);
  const [loadingNara, setLoadingNara] = useState(false);
  const [showNaraSugerencias, setShowNaraSugerencias] = useState(false);

  // Estados del formulario
  const [formData, setFormData] = useState({
    tipo_consulta: 'primera_vez',
    motivo_consulta: '',
    padecimiento_actual: '',
    interrogatorio_aparatos_sistemas: {},
    exploracion_fisica: {},
    signos_vitales: {
      presion_arterial_sistolica: '',
      presion_arterial_diastolica: '',
      frecuencia_cardiaca: '',
      frecuencia_respiratoria: '',
      temperatura: '',
      peso: '',
      talla: '',
      saturacion_oxigeno: ''
    },
    impresion_diagnostica: '',
    plan_tratamiento: '',
    pronostico: '',
    indicaciones_paciente: ''
  });

  // Diagnósticos
  const [diagnosticos, setDiagnosticos] = useState([]);
  const [busquedaCie10, setBusquedaCie10] = useState('');
  const [resultadosCie10, setResultadosCie10] = useState([]);
  const [buscandoCie10, setBuscandoCie10] = useState(false);

  // Medicamentos
  const [medicamentos, setMedicamentos] = useState([]);
  const [nuevoMedicamento, setNuevoMedicamento] = useState({
    nombre: '',
    presentacion: '',
    dosis: '',
    via: 'oral',
    frecuencia: '',
    duracion: '',
    indicaciones: ''
  });

  // Vademécum - Búsqueda de medicamentos
  const [busquedaMedicamento, setBusquedaMedicamento] = useState('');
  const [resultadosVademecum, setResultadosVademecum] = useState([]);
  const [buscandoVademecum, setBuscandoVademecum] = useState(false);
  const [medicamentoSeleccionado, setMedicamentoSeleccionado] = useState(null);

  // Sugerencias IA
  const [sugerenciasIA, setSugerenciasIA] = useState([]);
  const [cargandoIA, setCargandoIA] = useState(false);

  const tabLabels = ['Motivo', 'Exploración', 'Diagnóstico', 'Tratamiento', 'Receta', 'Finalizar'];

  // Calcular IMC automáticamente
  useEffect(() => {
    const peso = parseFloat(formData.signos_vitales.peso);
    const talla = parseFloat(formData.signos_vitales.talla);
    if (peso > 0 && talla > 0) {
      const tallaM = talla / 100;
      const imc = (peso / (tallaM * tallaM)).toFixed(1);
      setFormData(prev => ({
        ...prev,
        signos_vitales: { ...prev.signos_vitales, imc }
      }));
    }
  }, [formData.signos_vitales.peso, formData.signos_vitales.talla]);

  // Pre-llenar datos desde Consulta por Voz
  useEffect(() => {
    if (open && datosIniciales) {
      const filtrarNulos = (obj) => {
        if (!obj) return {};
        const limpio = {};
        Object.entries(obj).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '') limpio[k] = String(v);
        });
        return limpio;
      };

      setFormData(prev => ({
        ...prev,
        motivo_consulta: datosIniciales.motivo_consulta || prev.motivo_consulta,
        padecimiento_actual: datosIniciales.padecimiento_actual || prev.padecimiento_actual,
        signos_vitales: { ...prev.signos_vitales, ...filtrarNulos(datosIniciales.signos_vitales) },
        exploracion_fisica: datosIniciales.exploracion_fisica || prev.exploracion_fisica,
        impresion_diagnostica: datosIniciales.impresion_diagnostica || prev.impresion_diagnostica,
        plan_tratamiento: datosIniciales.plan_tratamiento || prev.plan_tratamiento,
        indicaciones_paciente: datosIniciales.indicaciones_paciente || prev.indicaciones_paciente
      }));

      if (datosIniciales.diagnosticos_sugeridos?.length) {
        setDiagnosticos(datosIniciales.diagnosticos_sugeridos.map(d => ({
          codigo_cie10: d.codigo_cie10 || '',
          descripcion_cie10: d.descripcion || '',
          tipo_diagnostico: 'principal',
          certeza: 'presuntivo'
        })));
      }
    }
  }, [open, datosIniciales]);

  // Buscar CIE-10
  const buscarCie10 = useCallback(async (query) => {
    if (query.length < 2) {
      setResultadosCie10([]);
      return;
    }
    setBuscandoCie10(true);
    try {
      const res = await fetch(`${API_URL}/emr/cie10/buscar?q=${encodeURIComponent(query)}&limit=15`, {
        headers: { 'X-User-Id': userId }
      });
      if (res.ok) {
        const data = await res.json();
        setResultadosCie10(data.resultados || []);
      }
    } catch (err) {
      console.error('Error buscando CIE-10:', err);
    } finally {
      setBuscandoCie10(false);
    }
  }, [userId]);

  // Debounce para búsqueda CIE-10
  useEffect(() => {
    const timer = setTimeout(() => {
      if (busquedaCie10) {
        buscarCie10(busquedaCie10);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [busquedaCie10, buscarCie10]);

  // Buscar en Vademécum
  const buscarVademecum = useCallback(async (query) => {
    if (query.length < 2) {
      setResultadosVademecum([]);
      return;
    }
    setBuscandoVademecum(true);
    try {
      const res = await fetch(`${API_URL}/emr/vademecum/buscar?q=${encodeURIComponent(query)}&limit=15`, {
        headers: { 'X-User-Id': userId }
      });
      if (res.ok) {
        const data = await res.json();
        setResultadosVademecum(data.medicamentos || []);
      }
    } catch (err) {
      console.error('Error buscando en vademécum:', err);
    } finally {
      setBuscandoVademecum(false);
    }
  }, [userId]);

  // Debounce para búsqueda vademécum
  useEffect(() => {
    const timer = setTimeout(() => {
      if (busquedaMedicamento) {
        buscarVademecum(busquedaMedicamento);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [busquedaMedicamento, buscarVademecum]);

  // Cuando se selecciona un medicamento del vademécum
  const handleSelectMedicamento = (medicamento) => {
    if (medicamento) {
      setMedicamentoSeleccionado(medicamento);
      setNuevoMedicamento({
        ...nuevoMedicamento,
        nombre: `${medicamento.nombre_generico} (${medicamento.nombre_comercial})`,
        presentacion: medicamento.forma_farmaceutica || '',
        dosis: medicamento.concentracion || '',
        via: medicamento.via_administracion?.toLowerCase()?.includes('oral') ? 'oral' :
             medicamento.via_administracion?.toLowerCase()?.includes('intra') ? 'intramuscular' :
             medicamento.via_administracion?.toLowerCase()?.includes('topic') ? 'topica' :
             medicamento.via_administracion?.toLowerCase()?.includes('oftal') ? 'oftalmica' :
             medicamento.via_administracion?.toLowerCase()?.includes('inhal') ? 'inhalada' : 'oral'
      });
      setBusquedaMedicamento('');
      setResultadosVademecum([]);
    }
  };

  // Agregar diagnóstico
  const agregarDiagnostico = (cie10, tipo = 'principal') => {
    if (diagnosticos.find(d => d.codigo_cie10 === cie10.codigo)) {
      setSnackbar({ open: true, message: 'Este diagnóstico ya fue agregado', severity: 'warning' });
      return;
    }
    setDiagnosticos([...diagnosticos, {
      codigo_cie10: cie10.codigo,
      descripcion_cie10: cie10.descripcion,
      tipo_diagnostico: tipo,
      certeza: 'presuntivo'
    }]);
    setBusquedaCie10('');
    setResultadosCie10([]);
  };

  // Obtener sugerencias de IA
  const obtenerSugerenciasIA = async () => {
    if (!formData.motivo_consulta && !formData.padecimiento_actual) {
      setSnackbar({ open: true, message: 'Ingresa el motivo de consulta primero', severity: 'warning' });
      return;
    }

    setCargandoIA(true);
    try {
      const sintomas = (formData.motivo_consulta + ' ' + formData.padecimiento_actual)
        .split(/[,.\n]/)
        .map(s => s.trim())
        .filter(s => s.length > 2);

      const res = await fetch(`${API_URL}/emr/ia/sugerir-diagnosticos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify({
          sintomas,
          edad: calcularEdad(paciente?.fecha_nacimiento),
          sexo: paciente?.sexo
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSugerenciasIA(data.sugerencias || []);
      }
    } catch (err) {
      console.error('Error obteniendo sugerencias IA:', err);
      setSnackbar({ open: true, message: 'Error al obtener sugerencias', severity: 'error' });
    } finally {
      setCargandoIA(false);
    }
  };

  // Agregar medicamento
  const agregarMedicamento = () => {
    if (!nuevoMedicamento.nombre || !nuevoMedicamento.dosis) {
      setSnackbar({ open: true, message: 'Nombre y dosis son requeridos', severity: 'warning' });
      return;
    }
    setMedicamentos([...medicamentos, { ...nuevoMedicamento }]);
    setNuevoMedicamento({
      nombre: '',
      presentacion: '',
      dosis: '',
      via: 'oral',
      frecuencia: '',
      duracion: '',
      indicaciones: ''
    });
    setMedicamentoSeleccionado(null);
    setBusquedaMedicamento('');
  };

  // Guardar consulta
  const guardarConsulta = async (finalizar = false) => {
    setGuardando(true);
    try {
      const consultaData = {
        expediente_id: expedienteId,
        medico_id: userId,
        tipo_consulta: formData.tipo_consulta,
        motivo_consulta: formData.motivo_consulta,
        padecimiento_actual: formData.padecimiento_actual,
        interrogatorio_aparatos_sistemas: formData.interrogatorio_aparatos_sistemas,
        exploracion_fisica: formData.exploracion_fisica,
        signos_vitales: formData.signos_vitales,
        impresion_diagnostica: formData.impresion_diagnostica,
        plan_tratamiento: formData.plan_tratamiento,
        pronostico: formData.pronostico,
        indicaciones_paciente: formData.indicaciones_paciente
      };

      let res;
      if (consultaId) {
        res = await fetch(`${API_URL}/emr/consultas/${consultaId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId
          },
          body: JSON.stringify(consultaData)
        });
      } else {
        res = await fetch(`${API_URL}/emr/consultas`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId
          },
          body: JSON.stringify(consultaData)
        });
      }

      if (!res.ok) {
        throw new Error('Error al guardar consulta');
      }

      const consultaGuardada = await res.json();
      setConsultaId(consultaGuardada.id);

      // Guardar diagnósticos
      for (const diag of diagnosticos) {
        if (!diag.id) {
          await fetch(`${API_URL}/emr/consultas/${consultaGuardada.id}/diagnosticos`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId
            },
            body: JSON.stringify(diag)
          });
        }
      }

      // Crear receta si hay medicamentos
      if (medicamentos.length > 0) {
        await fetch(`${API_URL}/emr/consultas/${consultaGuardada.id}/recetas`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId
          },
          body: JSON.stringify({
            medicamentos,
            indicaciones_generales: formData.indicaciones_paciente
          })
        });
      }

      // Finalizar si se solicita
      if (finalizar) {
        await fetch(`${API_URL}/emr/consultas/${consultaGuardada.id}/finalizar`, {
          method: 'POST',
          headers: { 'X-User-Id': userId }
        });
      }

      setSnackbar({
        open: true,
        message: finalizar ? 'Consulta finalizada correctamente' : 'Consulta guardada',
        severity: 'success'
      });

      if (finalizar && onConsultaCreated) {
        onConsultaCreated(consultaGuardada);
        // Fetch NARA post-consulta suggestions instead of closing immediately
        setLoadingNara(true);
        fetch(`${API_URL}/emr/ia/post-consulta/${consultaGuardada.id}`, {
          method: 'POST',
          headers: { 'X-User-Id': userId }
        })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data && data.sugerencias && data.sugerencias.length > 0) {
              setNaraSugerencias(data);
              setLoadingNara(false);
            } else {
              setLoadingNara(false);
              onClose();
            }
          })
          .catch(() => { setLoadingNara(false); onClose(); });
      }

    } catch (err) {
      console.error('Error:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const m = hoy.getMonth() - nacimiento.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) edad--;
    return edad;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "#f8fafc",
          boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
          border: "none",
          overflow: "hidden",
          minHeight: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header con gradiente */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
          color: "#fff",
          py: 2,
          px: 3
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <MedicalIcon sx={{ fontSize: 26, color: "#d97706" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 20, letterSpacing: 0.5 }}>
              Nueva Consulta Médica
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Avatar sx={{ width: 20, height: 20, bgcolor: '#fbbf24', fontSize: 10 }}>
                {paciente?.nombre_completo?.charAt(0)}
              </Avatar>
              <Typography sx={{ fontSize: 13, opacity: 0.9 }}>
                {paciente?.nombre_completo} • {calcularEdad(paciente?.fecha_nacimiento)} años • {paciente?.sexo}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Paso ${tabActual + 1} de 6`}
            size="small"
            sx={{
              background: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 11
            }}
          />
          <IconButton
            onClick={onClose}
            sx={{
              color: '#fff',
              '&:hover': { background: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Tabs estilizados */}
        <Box sx={{
          borderBottom: "1px solid #e2e8f0",
          background: "#fff",
          px: 2
        }}>
          <Tabs
            value={tabActual}
            onChange={(e, v) => setTabActual(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                minHeight: 56,
                color: '#64748b',
                '&.Mui-selected': {
                  color: '#1e3a5f'
                }
              },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab icon={<MedicalIcon sx={{ fontSize: 20 }} />} label="Motivo" iconPosition="start" />
            <Tab icon={<VitalsIcon sx={{ fontSize: 20 }} />} label="Exploración" iconPosition="start" />
            <Tab icon={<DiagnosisIcon sx={{ fontSize: 20 }} />} label="Diagnóstico" iconPosition="start" />
            <Tab icon={<HealingIcon sx={{ fontSize: 20 }} />} label="Tratamiento" iconPosition="start" />
            <Tab icon={<MedicationIcon sx={{ fontSize: 20 }} />} label="Receta" iconPosition="start" />
            <Tab icon={<CheckIcon sx={{ fontSize: 20 }} />} label="Finalizar" iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ overflowY: 'auto', maxHeight: 'calc(90vh - 220px)' }}>
          {/* Tab 0: Motivo de consulta */}
          <TabPanel value={tabActual} index={0}>
            <SeccionFormulario icon={<MedicalIcon />} titulo="Tipo de Consulta" color="#6366f1">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Tipo de Consulta"
                    value={formData.tipo_consulta}
                    onChange={(e) => setFormData({ ...formData, tipo_consulta: e.target.value })}
                    size="small"
                    sx={textFieldSx}
                  >
                    <MenuItem value="primera_vez">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                        Primera vez
                      </Box>
                    </MenuItem>
                    <MenuItem value="subsecuente">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                        Subsecuente
                      </Box>
                    </MenuItem>
                    <MenuItem value="urgencia">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                        Urgencia
                      </Box>
                    </MenuItem>
                    <MenuItem value="interconsulta">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8b5cf6' }} />
                        Interconsulta
                      </Box>
                    </MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </SeccionFormulario>

            <SeccionFormulario icon={<DescriptionIcon />} titulo="Motivo de la Consulta" color="#10b981">
              <TextField
                fullWidth
                label="¿Por qué acude el paciente?"
                multiline
                rows={3}
                value={formData.motivo_consulta}
                onChange={(e) => setFormData({ ...formData, motivo_consulta: e.target.value })}
                placeholder="Describa el motivo principal de la consulta..."
                required
                size="small"
                sx={textFieldSx}
              />
            </SeccionFormulario>

            <SeccionFormulario icon={<HealingIcon />} titulo="Padecimiento Actual" color="#f59e0b">
              <TextField
                fullWidth
                label="Historia del padecimiento"
                multiline
                rows={5}
                value={formData.padecimiento_actual}
                onChange={(e) => setFormData({ ...formData, padecimiento_actual: e.target.value })}
                placeholder="Descripción cronológica del padecimiento, síntomas, evolución, tratamientos previos..."
                size="small"
                sx={textFieldSx}
              />
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<MicIcon />}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    '&:hover': { borderColor: '#6366f1', color: '#6366f1', background: '#f1f5f9' }
                  }}
                >
                  Grabar Audio
                </Button>
                <Button
                  variant="outlined"
                  startIcon={cargandoIA ? <CircularProgress size={16} /> : <PsychologyIcon />}
                  onClick={obtenerSugerenciasIA}
                  disabled={cargandoIA}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    borderColor: '#10b981',
                    color: '#10b981',
                    '&:hover': { borderColor: '#059669', background: '#f0fdf4' }
                  }}
                >
                  Sugerencias IA
                </Button>
              </Box>
            </SeccionFormulario>
          </TabPanel>

          {/* Tab 1: Exploración física */}
          <TabPanel value={tabActual} index={1}>
            <SeccionFormulario icon={<VitalsIcon />} titulo="Signos Vitales" color="#ef4444">
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="PA Sistólica"
                    type="number"
                    size="small"
                    value={formData.signos_vitales.presion_arterial_sistolica}
                    onChange={(e) => setFormData({
                      ...formData,
                      signos_vitales: { ...formData.signos_vitales, presion_arterial_sistolica: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">mmHg</InputAdornment> }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="PA Diastólica"
                    type="number"
                    size="small"
                    value={formData.signos_vitales.presion_arterial_diastolica}
                    onChange={(e) => setFormData({
                      ...formData,
                      signos_vitales: { ...formData.signos_vitales, presion_arterial_diastolica: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">mmHg</InputAdornment> }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Frecuencia Cardíaca"
                    type="number"
                    size="small"
                    value={formData.signos_vitales.frecuencia_cardiaca}
                    onChange={(e) => setFormData({
                      ...formData,
                      signos_vitales: { ...formData.signos_vitales, frecuencia_cardiaca: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">lpm</InputAdornment> }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Frecuencia Respiratoria"
                    type="number"
                    size="small"
                    value={formData.signos_vitales.frecuencia_respiratoria}
                    onChange={(e) => setFormData({
                      ...formData,
                      signos_vitales: { ...formData.signos_vitales, frecuencia_respiratoria: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">rpm</InputAdornment> }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Temperatura"
                    type="number"
                    size="small"
                    inputProps={{ step: 0.1 }}
                    value={formData.signos_vitales.temperatura}
                    onChange={(e) => setFormData({
                      ...formData,
                      signos_vitales: { ...formData.signos_vitales, temperatura: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Peso"
                    type="number"
                    size="small"
                    inputProps={{ step: 0.1 }}
                    value={formData.signos_vitales.peso}
                    onChange={(e) => setFormData({
                      ...formData,
                      signos_vitales: { ...formData.signos_vitales, peso: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Talla"
                    type="number"
                    size="small"
                    value={formData.signos_vitales.talla}
                    onChange={(e) => setFormData({
                      ...formData,
                      signos_vitales: { ...formData.signos_vitales, talla: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="IMC (calculado)"
                    size="small"
                    value={formData.signos_vitales.imc || '—'}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">kg/m²</InputAdornment>
                    }}
                    sx={{
                      ...textFieldSx,
                      '& .MuiOutlinedInput-root': {
                        ...textFieldSx['& .MuiOutlinedInput-root'],
                        background: '#f0fdf4'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="SpO2"
                    type="number"
                    size="small"
                    value={formData.signos_vitales.saturacion_oxigeno}
                    onChange={(e) => setFormData({
                      ...formData,
                      signos_vitales: { ...formData.signos_vitales, saturacion_oxigeno: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    sx={textFieldSx}
                  />
                </Grid>
              </Grid>
            </SeccionFormulario>

            <SeccionFormulario icon={<PersonIcon />} titulo="Exploración Física" color="#8b5cf6">
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Hallazgos de exploración física"
                size="small"
                value={formData.exploracion_fisica.descripcion || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  exploracion_fisica: { ...formData.exploracion_fisica, descripcion: e.target.value }
                })}
                placeholder="Cabeza y cuello: ...&#10;Tórax: ...&#10;Abdomen: ...&#10;Extremidades: ...&#10;Neurológico: ..."
                sx={textFieldSx}
              />
            </SeccionFormulario>
          </TabPanel>

          {/* Tab 2: Diagnóstico */}
          <TabPanel value={tabActual} index={2}>
            <Box sx={{ display: 'flex', gap: 0 }}>
              {/* Panel izquierdo: Búsqueda */}
              <Box sx={{ flex: 1, borderRight: '1px solid #e2e8f0' }}>
                <SeccionFormulario icon={<SearchIcon />} titulo="Búsqueda CIE-10" color="#3b82f6">
                  <TextField
                    fullWidth
                    label="Buscar diagnóstico"
                    size="small"
                    value={busquedaCie10}
                    onChange={(e) => setBusquedaCie10(e.target.value)}
                    placeholder="Escriba código o descripción..."
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                      endAdornment: buscandoCie10 && <CircularProgress size={18} />
                    }}
                    sx={textFieldSx}
                  />
                  <Box sx={{ maxHeight: 250, overflowY: 'auto', mt: 2 }}>
                    {resultadosCie10.map((cie) => (
                      <Box
                        key={cie.codigo}
                        onClick={() => agregarDiagnostico(cie)}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: 2,
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            background: '#f1f5f9',
                            borderColor: '#6366f1',
                            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={cie.codigo}
                            size="small"
                            sx={{
                              bgcolor: '#e0e7ff',
                              color: '#4338ca',
                              fontWeight: 600,
                              fontSize: 11
                            }}
                          />
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1e3a5f' }}>
                            {cie.descripcion_corta || cie.descripcion}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Sugerencias IA */}
                  {sugerenciasIA.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#166534', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PsychologyIcon sx={{ fontSize: 16 }} />
                        Sugerencias IA
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#166534', mb: 1.5 }}>
                        Basadas en los síntomas descritos. Confirme con su criterio médico.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {sugerenciasIA.map((sug, idx) => (
                          <Chip
                            key={idx}
                            label={`${sug.codigo_cie10}: ${sug.descripcion}`}
                            onClick={() => agregarDiagnostico({ codigo: sug.codigo_cie10, descripcion: sug.descripcion })}
                            size="small"
                            sx={{
                              cursor: 'pointer',
                              bgcolor: sug.probabilidad === 'alta' ? '#dcfce7' : sug.probabilidad === 'media' ? '#fef9c3' : '#f1f5f9',
                              color: sug.probabilidad === 'alta' ? '#166534' : sug.probabilidad === 'media' ? '#854d0e' : '#475569',
                              fontSize: 11,
                              '&:hover': { opacity: 0.8 }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </SeccionFormulario>
              </Box>

              {/* Panel derecho: Diagnósticos agregados */}
              <Box sx={{ flex: 1 }}>
                <SeccionFormulario icon={<DiagnosisIcon />} titulo={`Diagnósticos (${diagnosticos.length})`} color="#10b981">
                  {diagnosticos.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <DiagnosisIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                      <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                        Busque y agregue diagnósticos CIE-10
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {diagnosticos.map((diag, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: '#f0fdf4',
                            border: '1px solid #bbf7d0'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={diag.codigo_cie10} size="small" sx={{ bgcolor: '#10b981', color: '#fff', fontWeight: 600 }} />
                              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{diag.descripcion_cie10}</Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => setDiagnosticos(diagnosticos.filter((_, i) => i !== idx))}
                              sx={{ color: '#ef4444' }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              select
                              size="small"
                              value={diag.tipo_diagnostico}
                              onChange={(e) => {
                                const newDiags = [...diagnosticos];
                                newDiags[idx].tipo_diagnostico = e.target.value;
                                setDiagnosticos(newDiags);
                              }}
                              sx={{ width: 130, ...textFieldSx }}
                            >
                              <MenuItem value="principal">Principal</MenuItem>
                              <MenuItem value="secundario">Secundario</MenuItem>
                            </TextField>
                            <TextField
                              select
                              size="small"
                              value={diag.certeza}
                              onChange={(e) => {
                                const newDiags = [...diagnosticos];
                                newDiags[idx].certeza = e.target.value;
                                setDiagnosticos(newDiags);
                              }}
                              sx={{ width: 130, ...textFieldSx }}
                            >
                              <MenuItem value="presuntivo">Presuntivo</MenuItem>
                              <MenuItem value="probable">Probable</MenuItem>
                              <MenuItem value="definitivo">Definitivo</MenuItem>
                            </TextField>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </SeccionFormulario>

                <SeccionFormulario icon={<DescriptionIcon />} titulo="Impresión Diagnóstica" color="#f59e0b">
                  <TextField
                    fullWidth
                    label="Resumen diagnóstico"
                    multiline
                    rows={3}
                    size="small"
                    value={formData.impresion_diagnostica}
                    onChange={(e) => setFormData({ ...formData, impresion_diagnostica: e.target.value })}
                    placeholder="Resumen de la impresión diagnóstica..."
                    sx={textFieldSx}
                  />
                </SeccionFormulario>
              </Box>
            </Box>
          </TabPanel>

          {/* Tab 3: Tratamiento */}
          <TabPanel value={tabActual} index={3}>
            <SeccionFormulario icon={<HealingIcon />} titulo="Plan de Tratamiento" color="#6366f1">
              <TextField
                fullWidth
                label="Indicaciones terapéuticas"
                multiline
                rows={4}
                size="small"
                value={formData.plan_tratamiento}
                onChange={(e) => setFormData({ ...formData, plan_tratamiento: e.target.value })}
                placeholder="Tratamiento farmacológico y no farmacológico, estudios solicitados, referencias..."
                sx={textFieldSx}
              />
            </SeccionFormulario>

            <SeccionFormulario icon={<DiagnosisIcon />} titulo="Pronóstico" color="#8b5cf6">
              <TextField
                fullWidth
                label="Pronóstico del paciente"
                multiline
                rows={2}
                size="small"
                value={formData.pronostico}
                onChange={(e) => setFormData({ ...formData, pronostico: e.target.value })}
                placeholder="Bueno, reservado, malo..."
                sx={textFieldSx}
              />
            </SeccionFormulario>

            <SeccionFormulario icon={<PersonIcon />} titulo="Indicaciones al Paciente" color="#10b981">
              <TextField
                fullWidth
                label="Recomendaciones"
                multiline
                rows={3}
                size="small"
                value={formData.indicaciones_paciente}
                onChange={(e) => setFormData({ ...formData, indicaciones_paciente: e.target.value })}
                placeholder="Cuidados generales, dieta, signos de alarma, próxima cita..."
                sx={textFieldSx}
              />
            </SeccionFormulario>
          </TabPanel>

          {/* Tab 4: Receta */}
          <TabPanel value={tabActual} index={4}>
            <SeccionFormulario icon={<AddIcon />} titulo="Agregar Medicamento" color="#6366f1">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    freeSolo
                    options={resultadosVademecum}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option :
                      `${option.nombre_generico} (${option.nombre_comercial}) - ${option.concentracion || ''}`
                    }
                    filterOptions={(x) => x}
                    inputValue={busquedaMedicamento || nuevoMedicamento.nombre}
                    onInputChange={(event, newValue, reason) => {
                      if (reason === 'input') {
                        setBusquedaMedicamento(newValue);
                        if (!newValue) {
                          setNuevoMedicamento({ ...nuevoMedicamento, nombre: '' });
                        }
                      }
                    }}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                        setNuevoMedicamento({ ...nuevoMedicamento, nombre: newValue });
                      } else if (newValue) {
                        handleSelectMedicamento(newValue);
                      }
                    }}
                    loading={buscandoVademecum}
                    loadingText="Buscando medicamentos..."
                    noOptionsText={busquedaMedicamento?.length >= 2 ? "No se encontraron medicamentos" : "Escriba para buscar..."}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MedicationIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                            <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1e3a5f' }}>
                              {option.nombre_generico}
                            </Typography>
                            <Chip
                              label={option.nombre_comercial}
                              size="small"
                              sx={{ height: 20, fontSize: 10, bgcolor: '#e0e7ff', color: '#4338ca' }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                              {option.concentracion} • {option.forma_farmaceutica} • {option.via_administracion}
                            </Typography>
                            {option.controlado && (
                              <Chip label="Controlado" size="small" sx={{ height: 16, fontSize: 9, bgcolor: '#fef2f2', color: '#dc2626' }} />
                            )}
                          </Box>
                          <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.3 }}>
                            {option.categoria} • {option.laboratorio}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar Medicamento"
                        size="small"
                        placeholder="Escriba nombre genérico o comercial..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: '#94a3b8' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {buscandoVademecum && <CircularProgress size={18} />}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                        sx={textFieldSx}
                      />
                    )}
                  />
                  {medicamentoSeleccionado && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #bbf7d0' }}>
                      <Typography sx={{ fontSize: 11, color: '#166534' }}>
                        Seleccionado: {medicamentoSeleccionado.nombre_generico} ({medicamentoSeleccionado.nombre_comercial})
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label="Presentación"
                    size="small"
                    value={nuevoMedicamento.presentacion}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, presentacion: e.target.value })}
                    placeholder="Tab, Cap..."
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label="Dosis"
                    size="small"
                    value={nuevoMedicamento.dosis}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, dosis: e.target.value })}
                    placeholder="500mg"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Vía"
                    size="small"
                    value={nuevoMedicamento.via}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, via: e.target.value })}
                    sx={textFieldSx}
                  >
                    <MenuItem value="oral">Oral</MenuItem>
                    <MenuItem value="intramuscular">IM</MenuItem>
                    <MenuItem value="intravenosa">IV</MenuItem>
                    <MenuItem value="subcutanea">SC</MenuItem>
                    <MenuItem value="topica">Tópica</MenuItem>
                    <MenuItem value="oftalmica">Oftálmica</MenuItem>
                    <MenuItem value="inhalada">Inhalada</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label="Frecuencia"
                    size="small"
                    value={nuevoMedicamento.frecuencia}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, frecuencia: e.target.value })}
                    placeholder="c/8h"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label="Duración"
                    size="small"
                    value={nuevoMedicamento.duracion}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, duracion: e.target.value })}
                    placeholder="7 días"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Indicaciones específicas"
                    size="small"
                    value={nuevoMedicamento.indicaciones}
                    onChange={(e) => setNuevoMedicamento({ ...nuevoMedicamento, indicaciones: e.target.value })}
                    placeholder="Tomar con alimentos, antes de dormir..."
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={agregarMedicamento}
                    sx={{
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      fontWeight: 600,
                      '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }
                    }}
                  >
                    Agregar
                  </Button>
                </Grid>
              </Grid>
            </SeccionFormulario>

            <SeccionFormulario icon={<MedicationIcon />} titulo={`Medicamentos en Receta (${medicamentos.length})`} color="#f59e0b">
              {medicamentos.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <MedicationIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                  <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                    No se han agregado medicamentos
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {medicamentos.map((med, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        border: '1px solid #fcd34d'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: '#92400e', fontSize: 15 }}>
                            {med.nombre} {med.presentacion} - {med.dosis}
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: '#a16207', mt: 0.5 }}>
                            Vía {med.via} • {med.frecuencia} • {med.duracion}
                          </Typography>
                          {med.indicaciones && (
                            <Typography sx={{ fontSize: 12, color: '#92400e', mt: 0.5, fontStyle: 'italic' }}>
                              {med.indicaciones}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => setMedicamentos(medicamentos.filter((_, i) => i !== idx))}
                          sx={{ color: '#dc2626' }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </SeccionFormulario>
          </TabPanel>

          {/* Tab 5: Finalizar */}
          <TabPanel value={tabActual} index={5}>
            <SeccionFormulario icon={<WarningIcon />} titulo="Verificación Final" color="#f59e0b">
              <Alert
                severity="warning"
                sx={{
                  borderRadius: 2,
                  '& .MuiAlert-message': { width: '100%' }
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>
                  Antes de finalizar, verifique:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <li>Motivo de consulta registrado</li>
                  <li>Signos vitales completos</li>
                  <li>Al menos un diagnóstico CIE-10</li>
                  <li>Plan de tratamiento definido</li>
                </Box>
              </Alert>
            </SeccionFormulario>

            <SeccionFormulario icon={<DiagnosisIcon />} titulo="Resumen de la Consulta" color="#6366f1">
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>Tipo</Typography>
                    <Chip
                      label={formData.tipo_consulta.replace('_', ' ')}
                      size="small"
                      sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 600 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>Diagnósticos</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#10b981' }}>
                      {diagnosticos.length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#fef3c7', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>Medicamentos</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#d97706' }}>
                      {medicamentos.length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>Estado</Typography>
                    <Chip
                      label="En proceso"
                      size="small"
                      sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </SeccionFormulario>

            <Box sx={{ px: 3, py: 3, background: '#fff', display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<SaveIcon />}
                onClick={() => guardarConsulta(false)}
                disabled={guardando}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 4,
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': { background: '#f1f5f9', borderColor: '#94a3b8' }
                }}
              >
                Guardar Borrador
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={guardando ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                onClick={() => guardarConsulta(true)}
                disabled={guardando || !formData.motivo_consulta}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 4,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)'
                  },
                  '&:disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8'
                  }
                }}
              >
                Finalizar Consulta
              </Button>
            </Box>
          </TabPanel>
        </Box>
      </DialogContent>

      {/* Footer con navegación */}
      <DialogActions sx={{
        px: 3,
        py: 2,
        borderTop: "1px solid #e2e8f0",
        background: "#fff",
        justifyContent: 'space-between'
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            borderColor: "#e2e8f0",
            color: "#64748b",
            "&:hover": { background: "#f1f5f9", borderColor: "#94a3b8" }
          }}
        >
          Cancelar
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            disabled={tabActual === 0}
            onClick={() => setTabActual(tabActual - 1)}
            startIcon={<PrevIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              color: '#64748b',
              '&:hover': { background: '#f1f5f9' }
            }}
          >
            Anterior
          </Button>
          <Button
            variant="contained"
            disabled={tabActual === 5}
            onClick={() => setTabActual(tabActual + 1)}
            endIcon={<NextIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #2d5a87 0%, #3d6a97 100%)' },
              '&:disabled': { background: '#e2e8f0', color: '#94a3b8' }
            }}
          >
            Siguiente
          </Button>
        </Box>
      </DialogActions>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* NARA Post-Consulta Overlay */}
      {(loadingNara || naraSugerencias) && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: 'rgba(15, 23, 42, 0.92)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1400,
          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <Box sx={{ width: { xs: '90%', sm: 520 }, maxWidth: '100%' }}>
            <NaraTerminalCard
              label="nara@post-consulta ~ sugerencias"
              loading={loadingNara}
              loadingText="Analizando consulta"
              sx={{ mb: 0 }}
            >
              {naraSugerencias && (
                <Box>
                  <NaraSimpleTypewriter
                    text={naraSugerencias.resumen}
                    onComplete={() => setShowNaraSugerencias(true)}
                  />
                  {showNaraSugerencias && naraSugerencias.sugerencias && (
                    <Box sx={{
                      mt: 1.5, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)',
                      '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                      animation: 'fadeIn 0.3s ease-out'
                    }}>
                      {naraSugerencias.sugerencias.map((sug, idx) => {
                        const tipoColors = {
                          seguimiento: '#0ea5e9',
                          whatsapp: '#25D366',
                          laboratorio: '#f59e0b',
                          alerta: '#ef4444'
                        };
                        const color = tipoColors[sug.tipo] || '#64748b';
                        return (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                            <Chip
                              label={sug.tipo}
                              size="small"
                              sx={{
                                ...monoStyle, fontSize: 8.5, fontWeight: 700, height: 18,
                                bgcolor: `${color}22`,
                                color: color,
                                border: `1px solid ${color}44`,
                                minWidth: 75, mt: 0.2
                              }}
                            />
                            <Typography sx={{
                              ...monoStyle,
                              fontSize: 11, color: '#cbd5e1', lineHeight: 1.5, flex: 1
                            }}>
                              {sug.texto}
                            </Typography>
                            {sug.prioridad === 'alta' && (
                              <WarningIcon sx={{ fontSize: 14, color: '#ef4444', mt: 0.3, flexShrink: 0 }} />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </NaraTerminalCard>

            {/* Botón cerrar */}
            {(naraSugerencias && showNaraSugerencias) && (
              <Box sx={{ textAlign: 'center', mt: 1,
                '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                animation: 'fadeIn 0.3s ease-out 0.2s both'
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { setNaraSugerencias(null); setShowNaraSugerencias(false); onClose(); }}
                  sx={{
                    ...monoStyle,
                    color: '#64748b',
                    borderColor: 'rgba(100, 116, 139, 0.3)',
                    fontSize: 11,
                    borderRadius: 2,
                    px: 3,
                    '&:hover': { borderColor: '#10b981', color: '#10b981' }
                  }}
                >
                  Cerrar
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default ConsultaForm;
